<template>
  <el-table
    ref="ediTable"
    :data="edis"
    style="width: 100%"
    size="small"
    :row-class-name="tableRowClassName"
    @expand-change="handleExpandChange"
  >
    <el-table-column type="expand">
      <template v-slot="{ row }">
        <ConsignmentTable
          v-if="row.consignments && row.consignments.length"
          :consignments="row.consignments"
        />

        <div v-else-if="row.import_cons === 0">
          No consignments found for this EDI.
        </div>

        <v-loading v-else>
          Loading consignments...
        </v-loading>
      </template>
    </el-table-column>

    <el-table-column
      label="Manifest Name"
      prop="import_file_name"
      sortable
      min-width="190"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="ID"
      prop="edi_id"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="Manifest"
      prop="import_manifest_no"
      min-width="150"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="Locked"
      prop="edi_status"
      width="90"
      align="center"
    >
      <template v-slot="{ row }">
        <el-tooltip
          v-if="row.edi_status === 1"
          content="Locked"
          placement="top"
        >
          <i class="el-icon-lock" />
        </el-tooltip>
        <span v-else>-</span>
      </template>
    </el-table-column>

    <el-table-column
      label="Account"
      sortable
      min-width="200"
      show-overflow-tooltip
    >
      <template v-slot="{ row }">
        <slot
          name="account"
          :edi="row"
        >
          <router-link
            v-if="row.account && row.account.id"
            :to="{ name:'manage-accounts-account', params: { accountId: row.account.id } }"
          >
            {{ row.account.name || '-' }}
          </router-link>
          <span v-else>-</span>
        </slot>
      </template>
    </el-table-column>

    <el-table-column
      label="Consignments"
      prop="import_cons"
      width="120"
      :formatter="(row, col, cellValue) => cellValue || 0"
    />

    <el-table-column
      label="Created Time"
      prop="import_stime"
      sortable
      width="170"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => formatDate(cellValue)"
    />

    <el-table-column
      label="Errors"
      prop="import_errors"
      show-overflow-tooltip
      min-width="140"
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="Duplicates"
      prop="import_duplicates"
      show-overflow-tooltip
      width="120"
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="App Name"
      prop="import_appinfo"
      sortable
      min-width="160"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="Carrier"
      sortable
      min-width="200"
      show-overflow-tooltip
    >
      <template v-slot="{ row }">
        <slot
          name="carrier"
          :edi="row"
        >
          <router-link
            v-if="row.carrier && row.carrier.id"
            :to="{ name:'manage-carriers-carrier', params: { carrierId: row.carrier.id } }"
          >
            {{ row.carrier.name || '-' }}
          </router-link>
          <span v-else>-</span>
        </slot>
      </template>
    </el-table-column>

    <el-table-column
      label="Status"
      prop="export_status"
      sortable
      min-width="120"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => getExportStatus(cellValue)"
    />

    <el-table-column
      label="Export Errors"
      prop="export_errors"
      min-width="180"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="Export Notes"
      prop="export_notes"
      min-width="180"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-'"
    />

    <el-table-column
      label="Export Time"
      prop="export_etime"
      width="170"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => formatDate(cellValue)"
    />

    <el-table-column
      fixed="right"
      label="Actions"
      align="right"
      width="130"
    >
      <template v-slot="{ row }">
        <slot
          name="actions"
          :edi="row"
        >
          <el-dropdown
            size="mini"
            split-button
            trigger="click"
            :loading="isProcessing(row)"
            @click="resend(row)"
            @command="handleActionCommand"
          >
            Resend

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item :command="{ action: regenerate, row }">
                Regenerate
              </el-dropdown-item>

              <el-dropdown-item
                :disabled="row.edi_status !== 1"
                :command="{ action: unlock, row }"
              >
                Unlock
              </el-dropdown-item>

              <el-dropdown-item :command="{ action: viewConsignments, row }">
                View Consignments
              </el-dropdown-item>

              <el-dropdown-item
                :disabled="!row.file || !row.file.id"
                :command="{ action: downloadFile, row }"
              >
                Download File
              </el-dropdown-item>

              <el-dropdown-item
                :disabled="!row.error_file || !row.error_file.id"
                :command="{ action: downloadErrorFile, row }"
              >
                Download Error File
              </el-dropdown-item>

              <el-dropdown-item
                :disabled="!row.consignments || row.consignments.length === 0"
                :command="{ action: printManifest, row }"
              >
                Print Manifest
              </el-dropdown-item>

              <el-dropdown-item
                :disabled="!row.consignments || row.consignments.length === 0"
                :command="{ action: downloadManifest, row }"
              >
                Download Manifest PDF
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </slot>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import ConsignmentTable from '@/components/Application/Freight/EDI/ConsignmentTable'

export default {
  components: {
    ConsignmentTable
  },
  props: {
    edis: {
      type: Array,
      required: true
    },
    processingEdiIds: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    tableRowClassName ({ row }) {
      // Orange pending/processing
      // red failed
      // white success
      if (row.export_status === 4) {
        return 'warning-row'
      } else if (row.export_status === 0) {
        return 'warning-row'
      } else if (row.export_status === 5) {
        return 'danger-row'
      } else if (row.export_status === 1) {
        return 'primary-row'
      }
      return ''
    },
    formatDate (value) {
      if (!value) {
        return '-'
      }

      const momentValue = this.$moment(value)
      return momentValue.isValid() ? momentValue.format('DD/MM/YYYY H:m:s') : '-'
    },
    isProcessing (edi) {
      return this.processingEdiIds.includes(edi.id)
    },
    handleActionCommand ({ action, row }) {
      action(row)
    },
    resend (row) {
      this.$emit('resend', row)
    },
    regenerate (row) {
      this.$emit('regenerate', row)
    },
    unlock (row) {
      this.$emit('unlock', row)
    },
    downloadFile (row) {
      this.$emit('download-file', row)
    },
    downloadErrorFile (row) {
      this.$emit('download-error-file', row)
    },
    downloadManifest (row) {
      this.$emit('download-manifest', row)
    },
    printManifest (row) {
      this.$emit('print-manifest', row)
    },
    viewConsignments (row) {
      this.$refs.ediTable.toggleRowExpansion(row, true)
      this.$emit('view-consignments', row)
    },
    handleExpandChange (row, expandedRows) {
      const isExpanded = expandedRows.some(expandedRow => expandedRow.id === row.id)

      if (isExpanded) {
        this.$emit('view-consignments', row)
      }
    },
    getExportStatus (value) {
      switch (value) {
        case 0:
          return 'Pending'
        case 1:
          return 'Pending'
        case 2:
          return 'File Created'
        case 3:
          return 'Success'
        case 4:
          return 'Failure'
        case 5:
          return 'Failure'
        default:
          return '-'
      }
    },
    getImportStatus (value) {
      switch (value) {
        default:
          return '-'
      }
    },
    getEDIStatus (value) {
      switch (value) {
        case 1:
          return 'Locked'
        default:
          return '-'
      }
    }
  }
}
</script>
<style>
  .el-table .warning-row {
    background: #fdf6ec;
  }

  .el-table .danger-row {
    background: rgba(246, 152, 152, 0.61);
  }

  .el-table .primary-row {
    background: rgba(128, 188, 248, 0.61);
  }

</style>
